import { Helmet } from 'react-helmet';
import logo from '../logo.svg';

// Main Component for the Palm Ark Product Page
const PalmArkProductPage = () => {

    // The CSS is embedded here for convenience and to keep the component self-contained.
    const styles = `
      .palm-ark-page {
        font-family: 'Inter', sans-serif;
        background-color: #0c0c0c;
        color: #F4F4F4;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }
   
      .palm-ark-background-animation {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        overflow: hidden;
      }
   
      .palm-ark-circle {
        position: absolute;
        border-radius: 50%;
        filter: blur(140px);
        opacity: 0.7;
      }
   
      .pa-c1 {
        width: 500px; height: 500px;
        background: #0cc0df; /* New Theme Color */
        top: 10%; left: 15%;
        animation: palmArkMove1 22s ease-in-out alternate infinite;
      }
   
      .pa-c2 {
        width: 600px; height: 600px;
        background: #0cc0df; /* New Theme Color */
        bottom: 10%; right: 15%;
        animation: palmArkMove2 28s ease-in-out alternate-reverse infinite;
      }
       
      .pa-c3 {
        width: 400px; height: 400px;
        background: #97d2fb; /* New Theme Accent Color */
        top: 40%; right: 20%;
        animation: palmArkMove3 25s ease-in-out alternate infinite;
      }
   
      @keyframes palmArkMove1 {
        from { transform: translate(0, 0) scale(1) rotate(0deg); }
        to { transform: translate(-10vw, 5vh) scale(1.1) rotate(30deg); }
      }
      @keyframes palmArkMove2 {
        from { transform: translate(0, 0) scale(1.1) rotate(0deg); }
        to { transform: translate(5vw, -10vh) scale(1) rotate(-40deg); }
      }
      @keyframes palmArkMove3 {
          from { transform: translate(0, 0) scale(1); }
          to { transform: translate(10vw, 10vh) scale(1.2); }
      }
   
      .palm-ark-wrapper {
        position: relative;
        z-index: 2;
        width: 100%;
        max-width: 1400px;
        padding: 1rem 2rem;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin: 0 auto;
        max-height: 100vh;
        overflow-y: auto;
        box-sizing: border-box;
      }
   
      .palm-ark-glass-box {
        background: rgba(30, 30, 35, 0.55);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-radius: 24px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 2rem;
        box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
      }
       
      .palm-ark-hero {
        text-align: center;
      }
   
      .palm-ark-hero h1 {
          font-size: 3rem;
          font-weight: 700;
          background: -webkit-linear-gradient(45deg, #0cc0df, #97d2fb);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.75rem;
      }
   
      .palm-ark-hero p {
          font-size: 1.1rem;
          max-width: 700px;
          margin: 0 auto;
          opacity: 0.9;
      }
   
      .palm-ark-features-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
      }
       
      .palm-ark-feature-box {
        text-align: left;
        display: flex;
        flex-direction: column;
        background: rgba(45, 45, 50, 0.6);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        padding: 1.5rem;
      }
       
      .palm-ark-feature-box:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.5);
      }
       
      .palm-ark-feature-box .icon {
          margin-bottom: 1rem;
          width: 40px;
          height: 40px;
      }
       
      .palm-ark-feature-box h3 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          color: #fff;
      }

      .palm-ark-feature-box p {
          font-size: 0.9rem;
          line-height: 1.4;
      }

      /* Floating Home Button */
      .floating-home-btn {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        z-index: 1000;
        background-color: rgba(12, 192, 223, 0.8); /* New Theme Color */
        color: white;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 20px rgba(0,0,0,0.4);
        border: 1px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(5px);
        -webkit-backdrop-filter: blur(5px);
        transition: transform 0.3s ease, background-color 0.3s ease;
      }

      .floating-home-btn:hover {
        transform: scale(1.1);
        background-color: rgba(12, 192, 223, 1); /* New Theme Color */
      }

      .floating-home-btn svg {
        width: 28px;
        height: 28px;
      }
   
      /* Responsive adjustments */
      @media (max-width: 768px) {
          .palm-ark-page {
            position: relative;
            height: auto;
          }
          .palm-ark-wrapper {
            padding: 1.5rem;
            gap: 2rem;
            overflow-y: visible;
            max-height: none;
          }
          .palm-ark-hero h1 {
              font-size: 2.5rem;
          }
          .palm-ark-hero p {
              font-size: 1.1rem;
          }
          .floating-home-btn {
            width: 50px;
            height: 50px;
            bottom: 1rem;
            right: 1rem;
          }
      }
    `;
   
    return (
      <>
        <Helmet>
            <meta charSet="utf-8" />
            <title>Palm Ark™ - PalmDigitalz</title>
            <link rel="canonical" href="http://palmdigitalz.com/palm-ark" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta name="description" content="Palm Ark: The World's 1st Modernization AI Engine" />
            <meta name="keywords" content="palm ark, Palm Ark, world's 1st modernization AI engine, mainframe modernization, app modernization, business rule generator, use case diagram generator, pseudo code generator, custom AI framework, LLM agnostic, Modernization AI Engine" />
            <link rel="icon" href={logo}></link>
        </Helmet>

        <style>{styles}</style>
        <div className="palm-ark-page">
          {/* SVG Gradient for Icons */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="palm-ark-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#0cc0df' }} />
                <stop offset="100%" style={{ stopColor: '#97d2fb' }} />
              </linearGradient>
            </defs>
          </svg>
   
          {/* Animated Background */}
          <div className="palm-ark-background-animation">
            <div className="palm-ark-circle pa-c1"></div>
            <div className="palm-ark-circle pa-c2"></div>
            <div className="palm-ark-circle pa-c3"></div>
          </div>
   
          {/* Page Content */}
          <div className="palm-ark-wrapper">
            <header className="palm-ark-hero palm-ark-glass-box">
              <h1>Palm Ark™</h1>
              <p>The world's first modernization AI engine.</p>
            </header>
   
            <main>
              <div className="palm-ark-features-grid">
                 
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                     <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14l-4-4 1.41-1.41L11 13.17l6.59-6.59L19 8l-8 8z" strokeWidth="1"/>
                  </svg>
                  <h3>Near Deterministic</h3>
                  <p>Delivers highly predictable and consistent results, crucial for enterprise-grade modernization projects.</p>
                </div>
   
                <div className="palm-ark-feature-box palm-ark-glass-box">
                   <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                     <path d="M12 8V4H8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <path d="M12 16v4h4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>AI Agnostic</h3>
                  <p>Flexibly integrates with various AI models, ensuring you always use the best-fit technology.</p>
                </div>
   
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                    <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Custom Framework</h3>
                  <p>Built on a proprietary framework designed specifically for the nuances of legacy system code and architecture.</p>
                </div>
                 
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                     <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <polyline points="14 2 14 8 20 8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <circle cx="10" cy="15" r="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <path d="M12 17l-1.5-1.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>RAG Enabled</h3>
                  <p>Utilizes Retrieval-Augmented Generation to ground responses in your actual codebase, improving accuracy.</p>
                </div>
   
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Data Privacy</h3>
                  <p>Processes your code within a secure environment, ensuring your intellectual property never leaves your control.</p>
                </div>
                 
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                     <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     <path d="M20 16L4 8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Less Hallucinations</h3>
                  <p>Specialized tuning and RAG minimize the risk of AI generating incorrect or nonexistent code.</p>
                </div>
                
                <div className="palm-ark-feature-box palm-ark-glass-box">
                  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="url(#palm-ark-icon-gradient)">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3>Tuned for Legacy Modernization</h3>
                  <p>Specifically optimized to understand and transform archaic languages like COBOL into modern architectures.</p>
                </div>
   
              </div>
            </main>
          </div>
          
          <a href="/" className="floating-home-btn" aria-label="Go to Homepage">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
             </svg>
          </a>

        </div>
      </>
    );
  };
   
  export default PalmArkProductPage;
